import type { DateValue } from "react-aria-components";

import { today } from "@internationalized/date";
import { type EditRequestFormData, Timezone } from "@nlc/shared-types";
import { useState } from "react";
import {
  DateField,
  DateInput,
  DateSegment,
  DialogTrigger,
  Group,
  Button as RAButton,
} from "react-aria-components";
import { Controller } from "react-hook-form";

import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Calendar } from "@/components/calendar/Calendar.tsx";
import { ChevronDownIcon, XIcon } from "@/components/icons.tsx";
import { Popover } from "@/components/popover";
import { useDateDisplay } from "@/features/request/edit/hooks/useDateDisplay";
import { useEditRequestFormContext } from "@/features/request/edit/hooks/useEditRequestForm";
import { useFieldUpdate } from "@/features/request/edit/hooks/useFieldUpdate";
import { dateFormatterEst } from "@/utils/dateFormatterEst.ts";

type DateFields = keyof Pick<
  EditRequestFormData,
  "dueDate" | "internalDueDate"
>;

export function DatePickerPopover({
  name,
  timezone = "America/New_York",
}: {
  name: DateFields;
  timezone?: string;
}) {
  const methods = useEditRequestFormContext();
  const [focusedValue, setFocusedValue] = useState<DateValue | null>(null);
  const { updateField } = useFieldUpdate();

  const { watch } = useEditRequestFormContext();
  const dateField = watch(name);
  const dateDisplay = useDateDisplay(dateField);

  return (
    <Controller
      control={methods.control}
      name={name}
      render={({ field: { name, onChange, value } }) => (
        <DialogTrigger>
          <RAButton className="outline-none whitespace-nowrap">
            <Badge
              isRounded
              size="small"
              variant={dateDisplay?.badgeVariant ?? "primary"}
            >
              {value ? (
                dateDisplay?.isPill ? (
                  <span>{dateDisplay?.label}</span>
                ) : (
                  <>
                    <span>
                      {dateDisplay?.label ??
                        dateFormatterEst(value.toDate(timezone)) ??
                        "Select date"}
                    </span>{" "}
                    <Badge.Icon>
                      <ChevronDownIcon />
                    </Badge.Icon>
                  </>
                )
              ) : (
                "Select a date"
              )}
            </Badge>
          </RAButton>

          <Popover className="px-5 py-4" placement="bottom start">
            <div className="mb-4 flex gap-2">
              <DateField
                aria-label="Select date"
                className="flex grow justify-between rounded-md border border-border-primary bg-bg-primary px-[14px] py-md  text-md text-text-primary"
                name={name}
                onChange={(e) => {
                  onChange(e);
                  void updateField(name, e);
                  setFocusedValue(e);
                }}
                value={value}
              >
                <Group>
                  <DateInput className="flex">
                    {(segment) => (
                      <DateSegment className="outline-none" segment={segment} />
                    )}
                  </DateInput>
                </Group>
                <Button
                  className="flex size-6 justify-center self-end rounded-full hover:bg-bg-tertiary"
                  onPress={() => {
                    setFocusedValue(null);
                    onChange(null);
                    void updateField(name, null);
                  }}
                  variant="unstyled"
                >
                  <XIcon className="m-auto size-4" />
                </Button>
              </DateField>

              <Button
                color="gray"
                onPress={() => {
                  const now = today(Timezone.EST);

                  setFocusedValue(now);
                  onChange(now);
                  void updateField(name, now);
                }}
                variant="secondary"
              >
                Today
              </Button>
            </div>
            <Calendar
              aria-label="Select date"
              focusedValue={focusedValue}
              onChange={(date) => {
                onChange(date);
                updateField(name, date);
                setFocusedValue(date);
              }}
              onFocusChange={setFocusedValue}
              timezone={Timezone.EST}
              value={value}
            />
          </Popover>
        </DialogTrigger>
      )}
    />
  );
}
